import { useState, useMemo } from "react";
import { computeItemPrice, money } from "@/lib/pricing";
import { api, apiError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, PlusCircle, ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const MAX_MB = 10;

const uploadOne = async (f) => {
  if (!f.type.startsWith("image/") && !/\.(heic|heif)$/i.test(f.name)) throw new Error(`"${f.name}" isn't an image. Please choose a JPG, PNG, WEBP or HEIC photo.`);
  if (f.size > MAX_MB * 1024 * 1024) throw new Error(`"${f.name}" is ${(f.size / 1024 / 1024).toFixed(1)}MB. Image too large (max ${MAX_MB}MB).`);
  const fd = new FormData();
  fd.append("file", f);
  const { data } = await api.post("/public-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
};

const uploadErrorMessage = (err) => {
  if (!err.response) return err.message && !err.isAxiosError ? err.message : "Couldn't reach the bakery server. Please check your internet connection and try again.";
  if (err.response.status === 413) return `Image too large (max ${MAX_MB}MB).`;
  if (err.response.status >= 500) return "Our photo storage is having a moment. Please try again in a minute.";
  return apiError(err.response.data?.detail);
};

export const CakeCustomizer = ({ style, onAdd, onBack }) => {
  const [sel, setSel] = useState({});
  const [uploading, setUploading] = useState(false);

  const setVal = (gid, val) =>
    setSel((s) => {
      const next = { ...s, [gid]: val };
      // clear any selections that depend on the group we just changed
      style.groups.forEach((gg) => { if (gg.depends_on === gid) delete next[gg.id]; });
      return next;
    });
  const toggleMulti = (gid, val) =>
    setSel((s) => {
      const arr = Array.isArray(s[gid]) ? s[gid] : [];
      const nextArr = arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
      const next = { ...s, [gid]: nextArr };
      // hide+clear any color choice whose trigger add-on is no longer selected
      style.groups.forEach((gg) => {
        if (gg.depends_on === gid && gg.show_when && !gg.show_when.some((v) => nextArr.includes(v))) delete next[gg.id];
      });
      return next;
    });

  const isVisible = (g) => {
    if (!g.depends_on) return true;
    const dep = sel[g.depends_on];
    const has = (val) => (Array.isArray(dep) ? dep.includes(val) : dep === val);
    if (g.show_when) return g.show_when.some(has);
    if (g.options_map) return Array.isArray(dep) ? dep.some((v) => !!g.options_map[v]) : !!g.options_map[dep];
    return true;
  };
  const optionsFor = (g) => (g.options_map ? (g.options_map[sel[g.depends_on]] || []) : g.options);

  const handleUpload = (gid) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      setVal(gid, await uploadOne(f));
    } catch (err) {
      toast.error(uploadErrorMessage(err));
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleMultiUpload = (gid) => async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const existing = Array.isArray(sel[gid]) ? sel[gid] : [];
    const room = Math.max(0, 8 - existing.length);
    const urls = [];
    for (const f of files.slice(0, room)) {
      try {
        urls.push(await uploadOne(f));
      } catch (err) {
        toast.error(uploadErrorMessage(err));
      }
    }
    setSel((s) => {
      const arr = Array.isArray(s[gid]) ? s[gid] : [];
      return { ...s, [gid]: [...arr, ...urls].slice(0, 8) };
    });
    if (files.length > room) toast.info("You can add up to 8 side photos");
    setUploading(false);
    e.target.value = "";
  };

  const removeImageAt = (gid, idx) =>
    setSel((s) => {
      const arr = Array.isArray(s[gid]) ? s[gid] : [];
      return { ...s, [gid]: arr.filter((_, i) => i !== idx) };
    });

  const price = useMemo(() => computeItemPrice(style, sel), [style, sel]);

  const isEmpty = (v) => v == null || v === "" || (Array.isArray(v) && v.length === 0);
  const visibleGroups = style.groups.filter(isVisible);
  const missing = visibleGroups.filter((g) => g.required && isEmpty(sel[g.id])).map((g) => g.label);
  const canAdd = missing.length === 0 && price > 0;

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
      <div>
        <button data-testid="customizer-back" onClick={onBack} className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-sugar-ink2 hover:text-sugar-pink">
          <ArrowLeft size={16} /> Back to styles
        </button>
        <div className="flex items-center gap-4">
          <img src={style.image} alt={style.name} className="h-20 w-20 rounded-2xl object-cover sugar-card-shadow" />
          <div>
            <h2 className="font-display text-3xl font-extrabold text-sugar-ink">{style.name}</h2>
            <p className="text-sm text-sugar-ink2">{style.tagline}</p>
          </div>
        </div>

        <div data-testid="live-price-top" className="mt-5 flex items-baseline gap-2 rounded-2xl border border-sugar-border bg-white px-5 py-4 sugar-card-shadow">
          <span className="font-display text-4xl font-extrabold text-sugar-pink">{money(price)}</span>
          <span className="text-xs font-semibold text-sugar-muted">updates as you customize</span>
        </div>

        <div className="mt-8 space-y-7">
          {visibleGroups.map((g) => {
            const opts = optionsFor(g);
            return (
            <div key={g.id} data-testid={`group-${g.id}`}>
              <label className="mb-2 block text-sm font-bold text-sugar-ink">
                {g.label} {g.required && <span className="text-sugar-pink">*</span>}
              </label>

              {(g.type === "select" || g.type === "checkbox") && g.multi && (
                <div className="grid gap-2 sm:grid-cols-2">
                  {opts.map((o) => {
                    const active = Array.isArray(sel[g.id]) && sel[g.id].includes(o.value);
                    return (
                      <div
                        key={o.value}
                        role="button"
                        tabIndex={0}
                        data-testid={`opt-${g.id}-${o.value}`}
                        onClick={() => toggleMulti(g.id, o.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleMulti(g.id, o.value); } }}
                        className={[
                          "flex cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-3 text-left text-sm font-medium transition-all",
                          active ? "border-sugar-pink bg-sugar-blush text-sugar-ink" : "border-sugar-border bg-white text-sugar-ink2 hover:border-sugar-pink/40",
                        ].join(" ")}
                      >
                        <Checkbox checked={active} className="pointer-events-none" />
                        {o.label}
                      </div>
                    );
                  })}
                </div>
              )}

              {g.type === "select" && !g.multi && (
                <div className="flex flex-wrap gap-2">
                  {opts.map((o) => {
                    const active = sel[g.id] === o.value;
                    return (
                      <button
                        key={o.value}
                        data-testid={`opt-${g.id}-${o.value}`}
                        onClick={() => setVal(g.id, o.value)}
                        className={[
                          "rounded-full border px-4 py-2 text-sm font-semibold transition-all",
                          active ? "border-sugar-pink bg-sugar-pink text-white shadow-sm" : "border-sugar-border bg-white text-sugar-ink2 hover:border-sugar-pink/50 hover:text-sugar-pink",
                        ].join(" ")}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {g.type === "image" && (
                <div>
                  {sel[g.id] ? (
                    <div className="relative inline-block">
                      <img src={`${BACKEND_URL}${sel[g.id]}`} alt="Inspiration" className="h-40 w-40 rounded-xl border border-sugar-border object-cover" />
                      <button data-testid={`remove-${g.id}`} onClick={() => setVal(g.id, "")} className="absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full bg-sugar-ink text-white shadow-md">
                        <X size={15} />
                      </button>
                    </div>
                  ) : (
                    <label data-testid={`upload-${g.id}`} className="flex h-32 w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-sugar-border bg-white text-sugar-ink2 transition-colors hover:border-sugar-pink/60">
                      {uploading ? <Loader2 size={22} className="animate-spin text-sugar-pink" /> : <ImagePlus size={22} className="text-sugar-pink" />}
                      <span className="text-sm font-semibold">{uploading ? "Uploading…" : "Tap to upload a photo"}</span>
                      <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={handleUpload(g.id)} />
                    </label>
                  )}
                  {g.placeholder && <p className="mt-2 text-xs text-sugar-muted">{g.placeholder}</p>}
                </div>
              )}

              {g.type === "images" && (
                <div>
                  <div className="flex flex-wrap gap-3">
                    {(Array.isArray(sel[g.id]) ? sel[g.id] : []).map((url, i) => (
                      <div key={i} className="relative">
                        <img src={`${BACKEND_URL}${url}`} alt={`Side ${i + 1}`} className="h-28 w-28 rounded-xl border border-sugar-border object-cover" />
                        <button data-testid={`remove-${g.id}-${i}`} onClick={() => removeImageAt(g.id, i)} className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-sugar-ink text-white shadow-md">
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                    {(!Array.isArray(sel[g.id]) || sel[g.id].length < 8) && (
                      <label data-testid={`upload-${g.id}`} className="flex h-28 w-28 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-sugar-border bg-white text-sugar-ink2 transition-colors hover:border-sugar-pink/60">
                        {uploading ? <Loader2 size={20} className="animate-spin text-sugar-pink" /> : <ImagePlus size={20} className="text-sugar-pink" />}
                        <span className="px-1 text-center text-xs font-semibold">{uploading ? "Uploading…" : "Add photo"}</span>
                        <input type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={handleMultiUpload(g.id)} />
                      </label>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-sugar-muted">{(Array.isArray(sel[g.id]) ? sel[g.id].length : 0)}/8 photos{g.placeholder ? ` · ${g.placeholder}` : ""}</p>
                </div>
              )}

              {g.type === "text" && (
                <Input
                  data-testid={`input-${g.id}`}
                  placeholder={g.placeholder}
                  value={sel[g.id] || ""}
                  onChange={(e) => setVal(g.id, e.target.value)}
                  className="rounded-xl border-sugar-border"
                />
              )}

              {g.type === "textarea" && (
                <Textarea
                  data-testid={`input-${g.id}`}
                  placeholder={g.placeholder}
                  value={sel[g.id] || ""}
                  onChange={(e) => setVal(g.id, e.target.value)}
                  className="rounded-xl border-sugar-border"
                />
              )}

              {g.hint && <p className="mt-2 text-xs leading-relaxed text-sugar-muted">{g.hint}</p>}
            </div>
            );
          })}
        </div>
      </div>

      {/* sticky summary */}
      <div className="lg:sticky lg:top-24 lg:h-fit">
        <div className="rounded-2xl border border-sugar-border bg-white p-6 sugar-card-shadow">
          <h3 className="font-display text-xl font-bold text-sugar-ink">Your cake</h3>
          <p className="mt-1 text-xs text-sugar-muted">Price updates as you customize</p>
          <div data-testid="live-price-display" className="my-5 font-display text-5xl font-extrabold text-sugar-pink">
            {money(price)}
          </div>
          {missing.length > 0 && (
            <p data-testid="customizer-missing" className="mb-3 rounded-lg bg-sugar-cream px-3 py-2 text-xs font-medium text-sugar-ink2">
              Still needed: {missing.join(", ")}
            </p>
          )}
          <Button
            data-testid="customizer-add-btn"
            disabled={!canAdd}
            onClick={() => onAdd({ style_id: style.id, style_name: style.name, image: style.image, selections: sel, unit_price: price })}
            className="w-full rounded-full bg-sugar-pink py-6 text-base font-bold text-white hover:bg-sugar-pinkhover disabled:opacity-40"
          >
            <PlusCircle size={18} className="mr-2" /> Add to cart
          </Button>
        </div>
      </div>
    </div>
  );
};
